import {
  addCopyButton,
  addLinkOrderIdButton,
  addLoadAccountDetailsButton,
  addLoadOrderDetailsButton,
  addRowLoadingAnimation,
  configureTableColumns,
  displayErrorPopup,
} from './main';
import { addSpinner, addTableSpinner, removeSpinners } from './sidebar';
import { getSearchParamsUrl, overwriteSearchParamsUrl } from './search_params';
import OrderTabs from '../../jsx/components/forms/OrderTabs';
import { initDataTable, exportButtons, col, selectColumn, executionStatusCreatedCell } from './datatable';
import { baseApiAxios, microservicesApiAxios } from './api';
import { isSearchEnabled } from './search_settings';

const linkRegexAccount = /(\d{12})/;
const linkRegexVPC = /(vpc-\w+)/;
const linkRegexHostedZone = /[hH]osted\s[zZ]one:?\s{1,2}([\w.-]+)/;
const linkRegex4Wheels = /4[wW]heels\s[pP]roject\s{1,3}([\w-]+)/;

export const orderTableColumns = [
  { id: 'select_col' },
  { id: 'id_col', name: 'Order Id' },
  { id: 'creation_date_col', name: 'Order Date' },
  { id: 'action_col', name: 'Action' },
  { id: 'description_col', name: 'Order Description' },
  { id: 'account_id_col', name: 'Account Id' },
  { id: 'proposer_col', name: 'Proposer' },
  { id: 'approved_col', name: 'Approved' },
  { id: 'approval_date_col', name: 'Approval Date' },
  { id: 'approver_col', name: 'Approver' },
  { id: 'approval_comment_col', name: 'Approval Comment' },
  { id: 'executed_col', name: 'Executed' },
  { id: 'execution_date_col', name: 'Execution Date' },
  { id: 'execution_response_col', name: 'Execution Response' },
  { id: 'email_sent_col', name: 'Email Sent' },
];

const detailRowsOrders = [];
const orderTypeaheadTokenHistory = {};

const footerDropdownSearchValues = {
  Approved: [
    { value: 'NEW', innerText: 'New' },
    { value: 'APPROVED', innerText: 'Approved' },
    { value: 'DECLINED', innerText: 'Declined' },
    { value: 'WAITING_FOR_APPROVAL', innerText: 'Waiting for Approval' },
    { value: 'TIMEOUT', innerText: 'Timeout' },
  ],
  Executed: [
    { value: 'NEW', innerText: 'New' },
    { value: 'IN_PROGRESS', innerText: 'In Progress' },
    { value: 'SUCCESS', innerText: 'Success' },
    { value: 'SUCCESS_NO_ACTIONS', innerText: 'Success - No Actions' },
    { value: 'FAILED', innerText: 'Failed' },
    { value: 'NOT_COMPLETED', innerText: 'Not Completed' },
    { value: 'CANCELED', innerText: 'Canceled' },
    { value: 'WAITING', innerText: 'Waiting' },
  ],
  'Email Sent': [
    { value: 'YES', innerText: 'Yes' },
    { value: 'NO', innerText: 'No' },
  ],
};

const orderDropdownColumns = ['Approved', 'Executed', 'Email Sent'];
const orderSearchColumns = [
  'Id',
  'Action',
  'Order Description',
  'Account Id',
  'Proposer',
  'Approver',
  'Approval Comment',
  'Execution Response',
];

/**
 * Loads order data with pagination support, handling both DynamoDB and OpenSearch backends
 * @param {Object} data - Table data request parameters from DataTables
 * @param {Function} callback - Callback function to return data to DataTables
 * @param {string} table - Table ID for storing pagination state
 */
function loadIncrementsOrders(data, callback, table) {
  const payload = createOrderPayload(data, table);

  // Start loading indicators
  addSpinner(false);
  addTableSpinner();

  // Determine search backend from session settings
  const isOpenSearchEnabled = isSearchEnabled('order_search_enable');
  console.debug(`using opensearch is ${isOpenSearchEnabled}`);

  // Get OpenSearch next_token if available for this position
  let nextToken = null;
  if (isOpenSearchEnabled && parseInt(data.start) > 0) {
    const tokensKey = `${table}_opensearch_tokens`;
    const tokensObj = JSON.parse(localStorage.getItem(tokensKey) || '{}');

    // Look for the previous page's token
    const prevPosition = parseInt(data.start) - parseInt(data.length);
    nextToken = tokensObj[prevPosition] || null;
  }

  // Prepare search parameters
  const openSearchParams = buildOpenSearchParams(data, nextToken);

  // Execute search with appropriate backend
  isOpenSearchEnabled
    ? searchOrdersOpenSearch(payload, openSearchParams, data, callback, table, isOpenSearchEnabled)
    : searchOrderDynamoDB(payload, data, callback, table, isOpenSearchEnabled);
}

/**
 * Creates the payload for order retrieval with pagination support
 * @param {Object} data - Table data request parameters
 * @param {string} table - Table ID for state storage
 * @returns {Object} Order retrieval payload
 */
function createOrderPayload(data, tableId) {
  const payload = {
    action: 'list-orders',
    table_data: data,
  };

  const tableInfo = localStorage[tableId] ? JSON.parse(localStorage[tableId]) : undefined;

  if (tableInfo) {
    const dataStartInt = parseInt(data.start);
    if (dataStartInt !== 0) {
      const tableInfoLastStartInt = parseInt(tableInfo.LastStart);
      let rowData;

      if (tableInfoLastStartInt < dataStartInt) {
        // Moving forward in pagination
        rowData = tableInfo.LastItem;
      } else if (tableInfoLastStartInt === dataStartInt) {
        // Same page (refresh)
        rowData = tableInfo.RefreshItem;
        if (tableInfo.RefreshScanForward === true) {
          payload['scan_forward'] = tableInfo.RefreshScanForward;
        }
      } else {
        // Moving backward in pagination
        rowData = tableInfo.FirstItem;
        payload['scan_forward'] = true;
      }

      if (rowData) {
        payload['exclusive_start_key'] = rowData;
      }
    }
  }

  return payload;
}

/**
 * Builds OpenSearch parameters from DataTables request
 * @param {Object} data - Table data request parameters
 * @param {string|null} nextToken - Next token for pagination from previous response
 * @returns {Object} OpenSearch parameters
 */
function buildOpenSearchParams(data, nextToken = null) {
  const filters = buildOpenSearchFilters(data.columns);

  return {
    size: data.length,
    next_token: nextToken, // Use next_token instead of from/start
    filters: JSON.stringify(filters),
    sort: 'creation_date:desc',
  };
}

/**
 * Builds filters for OpenSearch from column data
 * @param {Array} columns - Column definitions with search values
 * @returns {Object} Filter object for OpenSearch
 */
function buildOpenSearchFilters(columns) {
  return columns
    .filter(col => col.data && col.search.value)
    .reduce((obj, col) => {
      // Special handling for different field types
      switch (col.data) {
        case 'email_sent': {
          // Convert YES/NO to boolean
          const emailSent = col.search.value === 'YES';
          obj[col.data] = { eq: emailSent };
          break;
        }
        case 'approved':
        case 'executed': {
          // Standardize format for status fields
          obj[col.data] = { eq: col.search.value.toUpperCase().replace(' ', '_') };
          break;
        }
        default:
          // Default text search
          obj[col.data] = { contains: col.search.value };
          break;
      }
      return obj;
    }, {});
}

/**
 * Searches orders using OpenSearch with fallback to DynamoDB
 * @param {Object} payload - Request payload
 * @param {Object} openSearchParams - OpenSearch parameters
 * @param {Object} data - Original DataTables request data
 * @param {Function} callback - DataTables callback function
 * @param {string} table - Table ID
 * @param {boolean} isOpenSearchEnabled - Whether OpenSearch is enabled
 * @param {string|null} nextToken - Next token for pagination from previous response
 */
function searchOrdersOpenSearch(payload, openSearchParams, data, callback, table, isOpenSearchEnabled) {
  microservicesApiAxios
    .getSearchOrders(null, openSearchParams)
    .then(result => {
      // Store next_token in localStorage for this table to support pagination
      if (result.next_token) {
        // Create or update the tokens object for this table
        const tokensKey = `${table}_opensearch_tokens`;
        const tokensObj = JSON.parse(localStorage.getItem(tokensKey) || '{}');

        // Store token with the current start position as key
        tokensObj[data.start] = result.next_token;
        localStorage.setItem(tokensKey, JSON.stringify(tokensObj));
      }

      return processOrderResult(result, data, callback, table, payload, isOpenSearchEnabled);
    })
    .catch((error, text) => {
      // response is missing in multiple cases when OpenSearch is not available/in error
      // we can then just fallback to DynamoDB for any error
      if (isOpenSearchEnabled && error) {
        // Fallback to DynamoDB when OpenSearch fails
        console.debug('opensearch return error, retry search with dynamodb');
        console.debug('set order_search_enable to false');
        sessionStorage.setItem('order_search_enable', false);

        addSpinner(false);
        addTableSpinner();
        searchOrderDynamoDB(payload, data, callback, table, false);
      } else {
        displayErrorPopup(error, text);
      }
    })
    .finally(() => {
      removeSpinners();
    });
}

/**
 * Searches orders using DynamoDB
 * @param {Object} payload - Request payload
 * @param {Object} data - Original DataTables request data
 * @param {Function} callback - DataTables callback function
 * @param {string} table - Table ID
 * @param {boolean} isOpenSearchEnabled - Whether OpenSearch is enabled
 */
function searchOrderDynamoDB(payload, data, callback, table, isOpenSearchEnabled) {
  baseApiAxios
    .getOrders(payload)
    .then(result => processOrderResult(result, data, callback, table, payload, isOpenSearchEnabled))
    .catch(displayErrorPopup)
    .finally(() => {
      removeSpinners();
    });
}

/**
 * CLOUD-35639: Searches orders using the typeahead API for account-scoped queries.
 * Used only in AWS Account Details → Orders tab.
 * Mirrors searchOrdersOpenSearch behavior exactly but calls the typeahead endpoint
 * with additional query and fields params.
 * @param {string} accountId - The account ID to query orders for
 * @param {Object} data - DataTables request data
 * @param {Function} callback - DataTables callback function
 * @param {string} tableId - Table ID for state storage
 */
async function searchOrdersTypeahead(accountId, data, callback, tableId) {
  const payload = createOrderPayload(data, tableId);
  const dataStart = parseInt(data.start);
  const dataLength = parseInt(data.length);

  if (!orderTypeaheadTokenHistory[tableId] || dataStart === 0) {
    orderTypeaheadTokenHistory[tableId] = {};
  }

  const tokenHistory = orderTypeaheadTokenHistory[tableId];

  addSpinner(false);
  addTableSpinner();

  // Build same params as original OpenSearch flow (filters, sort, size, next_token)
  const params = buildOpenSearchParams(data);

  // Add account_id filter dynamically when available
  if (accountId) {
    const filters = JSON.parse(params.filters);
    filters.account_id = { eq: accountId };
    params.filters = JSON.stringify(filters);
  }

  // Pass actual search input text as query (not account ID)
  const searchValue = data.search?.value || '';
  if (searchValue) {
    params.query = searchValue;
  }

  try {
    if (dataStart > 0) {
      const prevPosition = dataStart - dataLength;
      const nextToken = tokenHistory[prevPosition] || null;
      if (nextToken) {
        params.next_token = nextToken;
      }
    }

    const result = await microservicesApiAxios.getSearchOrdersTypeahead(null, params);

    if (result.next_token) {
      tokenHistory[dataStart] = result.next_token;
    } else {
      delete tokenHistory[dataStart];
    }

    processOrderResult(result, data, callback, tableId, payload, true);
  } catch (_error) {
    // Fallback to DynamoDB when typeahead API fails
    console.debug('typeahead return error, retry search with dynamodb');
    addSpinner(false);
    addTableSpinner();
    searchOrderDynamoDB(payload, data, callback, tableId, false);
  } finally {
    removeSpinners();
  }
}

/**
 * Processes order results from either API source
 * @param {Object} result - API response containing orders
 * @param {Object} requestData - Original DataTables request data
 * @param {Function} callback - DataTables callback function
 * @param {string} tableId - Table ID for state storage
 * @param {Object} payload - Original request payload
 * @param {boolean} isOpenSearchEnabled - Whether OpenSearch is enabled
 */
function processOrderResult(result, requestData, callback, tableId, payload, isOpenSearchEnabled) {
  // Determine if we have orders from either source
  const hasOrders = isOpenSearchEnabled ? !!result.items?.length : !!result.orders?.length;
  let orderedData = [];

  if (hasOrders) {
    // Extract order data from the appropriate source
    orderedData = isOpenSearchEnabled ? result.items : result.orders;

    if (!isOpenSearchEnabled) {
      // Apply sort for DynamoDB pagination compatibility
      // The DynamoDB ScanIndexForward options will return elements in
      // the opposite order. In the portal the order (based on the
      // creation_date). This will re-order the items for one of the
      // directions.
      orderedData.sort(() => (payload['scan_forward'] ? -1 : 0));
    }

    // Update table state for pagination
    updateTableState(tableId, requestData, orderedData);
  }

  // Calculate total records from the appropriate source
  const ordersTotal = isOpenSearchEnabled ? result.total?.value : result.orders_total;

  // Return formatted data to DataTables
  callback({
    draw: parseInt(requestData.draw),
    data: orderedData,
    recordsTotal: parseInt(ordersTotal || 0),
    recordsFiltered: parseInt(ordersTotal || 0),
  });
}

/**
 * Updates table state in localStorage for pagination support
 * @param {string} tableId - Table ID for state storage
 * @param {Object} requestData - Original DataTables request data
 * @param {Array} orderedData - Processed order data
 */
function updateTableState(tableId, requestData, orderedData) {
  if (orderedData.length === 0) return;

  const tableInfo = localStorage[tableId] ? JSON.parse(localStorage[tableId]) : undefined;
  let refreshItem, refreshScanForward;

  if (tableInfo) {
    const dataStartInt = parseInt(requestData.start);
    const tableInfoLastStartInt = parseInt(tableInfo.LastStart);

    if (tableInfoLastStartInt !== dataStartInt) {
      if (tableInfoLastStartInt < dataStartInt) {
        refreshItem = tableInfo.LastItem;
        refreshScanForward = false;
      } else {
        refreshItem = tableInfo.FirstItem;
        refreshScanForward = true;
      }
    } else {
      refreshItem = tableInfo.RefreshItem;
      refreshScanForward = tableInfo.RefreshScanForward;
    }
  }

  // Store updated pagination state
  localStorage[tableId] = JSON.stringify({
    RefreshItem: refreshItem,
    RefreshScanForward: refreshScanForward,
    FirstItem: orderedData[0],
    LastItem: orderedData[orderedData.length - 1],
    LastStart: requestData.start,
  });
}

const hiddenInfoCol = { visible: false, defaultContent: '', orderable: false, searchable: false };

export function addOrderData(tableId, presetApproved, presetExecuted, presetEmailSent, options = {}) {
  // CLOUD-35639: Extract account-scoped options for table filtering and column visibility
  // accountId: if provided, filters table to show only orders for this account (used in Account Details page)
  // hideAccountIdColumn: if true, hides the Account Id column (used in Account Details to avoid redundancy)
  const { accountId = null, hideAccountIdColumn = false } = options;

  // When hiding Account Id column, exclude it from footer to match the column definitions
  const tableColumns = hideAccountIdColumn
    ? orderTableColumns.filter(c => c.id !== 'account_id_col')
    : orderTableColumns;
  configureTableColumns(tableId, tableColumns);

  const buttons = [
    ...exportButtons(),
    {
      extend: 'resetTable',
      titleAttr: 'Reset all filters in the table footer',
    },
    {
      text: 'Reload Orders',
      titleAttr: 'Reload orders (no-cache)',
      action: function (_e, dt) {
        // With serverSide, ajax.reload() shows the processing spinner,
        // fetches fresh data, and redraws in a single server request.
        // Don't use ajaxReload or methodReload — they call dt.clear().draw()
        // first, which triggers a redundant server-side request.
        dt.ajax.reload();
      },
    },
  ];

  const language = {
    processing:
      '<span class="spinner-border-container"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></span>',
    lengthMenu: 'Show _MENU_ entries',
    paginate: {
      last: '', // we want to hide the last button
      first: 'First',
      next: 'Next',
      previous: 'Previous',
    },
  };

  const layout = {
    topStart: 'pageLength',
    topEnd: ['colvisCustom'],
    bottomStart: {
      buttons: buttons,
    },
    bottomEnd: {
      paging: {
        numbers: false, // we want to hide the numbers
      },
    },
    bottom2Start: 'info',
  };

  // we hide the last button in the pagination
  // this is ugly but better than write our own paging
  const dt = initDataTable(
    tableId,
    [], // since we overwrite the layout, we don't need the buttons here
    [
      selectColumn(),
      col('id', 'id_col', 'Id', {
        defaultContent: '',
        orderable: false,
        createdCell: function (td) {
          addCopyButton(td);
          addLoadOrderDetailsButton(td);
          addLinkOrderIdButton(td);
        },
      }),
      col('creation_date', 'creation_date_col', 'Order Date', { defaultContent: '', orderable: false }),
      col('action', 'action_col', 'Action', {
        // CLOUD-35639: Show Action column by default in Account Details Orders tab
        visible: !!hideAccountIdColumn,
        defaultContent: '',
        orderable: false,
      }),
      col('payload.description', 'description_col', 'Order Description', {
        defaultContent: '',
        orderable: false,
        className: 'td-limit',
        createdCell: function (td, cellData) {
          if (!cellData) {
            td.setAttribute('title', 'No description available for this order');
            cellData = '-';
          } else {
            addCopyButton(td);
          }
        },
        render: function (description) {
          if (!description) {
            return '-';
          }
          return description
            .replace(linkRegexAccount, '<a href="?account_id=$1#accountdetails">$1</a>')
            .replace(linkRegexVPC, '<a href="?vpc_id=$1#networkdetails">$1</a>')
            .replace(linkRegexHostedZone, '<a href="?domain=$1#hostedzonedetails">$1</a>')
            .replace(linkRegex4Wheels, '<a href="?cluster_id=$1#4wheelsdetails">$1</a>');
        },
      }),
      // CLOUD-35639: Completely exclude Account Id column in Account Details Orders tab
      // so it does not appear in the Visible Columns dropdown
      ...(hideAccountIdColumn
        ? []
        : [
            col('account_id', 'account_id_col', 'Account Id', {
              visible: true,
              defaultContent: '',
              orderable: false,
              createdCell: function (td) {
                addCopyButton(td);
                addLoadAccountDetailsButton(td);
              },
              render: function (data) {
                return data || '-';
              },
            }),
          ]),
      col('proposer', 'proposer_col', 'Proposer', { defaultContent: '', orderable: false }),
      col('approved', 'approved_col', 'Approved', {
        defaultContent: '',
        orderable: false,
        render: function (data) {
          return data.replace(/_/g, ' ').toUpperCase();
        },
        createdCell: function (td, cellData) {
          addCopyButton(td);
          switch (cellData) {
            case 'APPROVED':
              $(td).addClass('portal-success');
              break;
            case 'DECLINED':
            case 'TIMEOUT':
              $(td).addClass('portal-danger');
              break;
            default:
              $(td).addClass('portal-neutral');
              break;
          }
        },
      }),
      col('approval_date', 'approval_date_col', 'Approval Date', hiddenInfoCol),
      col('approver', 'approver_col', 'Approver', hiddenInfoCol),
      col('approval_comment', 'approval_comment_col', 'Approval Comment', hiddenInfoCol),
      col('executed', 'executed_col', 'Executed', {
        defaultContent: '',
        orderable: false,
        render: function (data) {
          return data.replace(/_/g, ' ').toUpperCase();
        },
        createdCell: executionStatusCreatedCell,
      }),
      col('execution_date', 'execution_date_col', 'Execution Date', hiddenInfoCol),
      col('execution_response', 'execution_response_col', 'Execution Response', hiddenInfoCol),
      col('email_sent', 'email_sent_col', 'Email Sent', {
        visible: false,
        defaultContent: '',
        orderable: false,
        render(data) {
          return data ? 'YES' : 'NO';
        },
        createdCell: function (td, cellData) {
          addCopyButton(td);
          if (cellData) {
            $(td).addClass('portal-success');
          } else {
            $(td).addClass('portal-neutral');
          }
        },
      }),
    ],
    null,
    {
      layout: layout,
      language: language,
      serverSide: true,
      // CLOUD-35639: Disable state persistence for Account Details Orders tab
      // to prevent stale search values from being restored on tab reload.
      // Sidebar Orders keeps stateSave enabled (default from initDataTable).
      ...(hideAccountIdColumn && { stateSave: false }),
      search: {
        regex: false,
      },
      lengthMenu: [
        [10, 25, 50, 100],
        [10, 25, 50, 100],
      ],
      ajax: (data, callback) => {
        if (accountId || hideAccountIdColumn) {
          // CLOUD-35639: Account Details Orders tab always uses typeahead API
          searchOrdersTypeahead(accountId, data, callback, tableId);
        } else {
          // Sidebar → Orders → List Orders: use original API flow
          loadIncrementsOrders(data, callback, tableId);
        }
      },
      initComplete: function () {
        const columnsState = this.api().state().columns;

        // Clear any stale column search state WITHOUT triggering a redraw.
        // DataTables already fired the initial ajax call; calling .draw()
        // here would cause a duplicate API request.
        this.api().columns().search('');

        this.api()
          .columns()
          .every(function (col) {
            const that = this;
            const column = this;
            const title = dt.column(col).header().textContent;

            if (orderDropdownColumns.indexOf(title) > -1) {
              const elementId = title.toLowerCase().replace(' ', '_');
              const select = $('<select id="' + elementId + '"><option value=""></option></select>')
                .appendTo($(column.footer()).empty())
                .on('change', function () {
                  column.search($(this).val()).draw();
                  if (getSearchParamsUrl(this.id) !== this.value)
                    overwriteSearchParamsUrl({ [this.id]: this.value.toLowerCase() });
                });

              if (footerDropdownSearchValues[title]) {
                footerDropdownSearchValues[title].forEach(function (data) {
                  let opt = document.createElement('option');
                  opt.innerText = data.innerText;
                  opt.value = data.value;
                  select.append(opt);
                });
              }
            }

            if (orderSearchColumns.indexOf(title) > -1) {
              // CLOUD-35639: Skip adding footer search input for Account Id column when in account-scoped mode
              // Since the account is already filtered at the column level (search property), users don't need
              // an additional search input in the footer. Hiding it keeps the UI clean in Account Details.
              if (title === 'Account Id' && hideAccountIdColumn) {
                return;
              }

              $(dt.column(col).footer()).html('<input type="text" placeholder="Search ' + title + '" />');

              if (columnsState[col].search && columnsState[col].search.search) {
                dt.column(col).footer().lastChild.value = columnsState[col].search.search;
              }

              $('input', this.footer()).on('keyup change', function (event) {
                if (event.keyCode === 13) {
                  if (that.search() !== this.value) {
                    that.search(this.value).draw();
                  }
                }
              });
            }
          });

        if (presetApproved) {
          const approvedAllowedValues = footerDropdownSearchValues['Approved'].map(obj => obj.value);
          const searchText = presetApproved.toUpperCase();
          if (approvedAllowedValues.indexOf(searchText) > -1) {
            const column = this.api().columns(7);
            column.search(searchText).draw();
            const dropdown = $(column.footer()).find('select');
            dropdown.val(searchText);
          }
        }

        if (presetExecuted) {
          const executedAllowedValues = footerDropdownSearchValues['Executed'].map(obj => obj.value);
          const searchText = presetExecuted.toUpperCase();
          if (executedAllowedValues.indexOf(searchText) > -1) {
            const column = this.api().columns(11);
            column.search(searchText).draw();
            const dropdown = $(column.footer()).find('select');
            dropdown.val(searchText);
          }
        }

        if (presetEmailSent) {
          const emailSentAllowedValues = footerDropdownSearchValues['Email Sent'].map(obj => obj.value);
          const searchText = presetEmailSent.toUpperCase();
          if (emailSentAllowedValues.indexOf(searchText) > -1) {
            const column = this.api().columns(14);
            column.search(searchText).draw();
            const dropdown = $(column.footer()).find('select');
            dropdown.val(searchText);
          }
        }

        $(window).trigger('resize');
      },
    },
  );

  // Array to track the ids of the details displayed rows
  // var detailRows = [];

  $('#' + tableId + ' tbody').on('click', 'tr td.details-control', function () {
    const tr = $(this).closest('tr');
    const row = dt.row(tr);
    const idx = $.inArray(tr.attr('id'), detailRowsOrders);

    if (row.child.isShown()) {
      tr.removeClass('details');
      row.child.hide();

      // Remove from the 'open' array
      detailRowsOrders.splice(idx, 1);
    } else {
      tr.addClass('details');
      addRowLoadingAnimation(row);
      row.child(<OrderTabs data={row.data()} />).show();
      row.child()[0].setAttribute('class', 'rowDetails');
      // Add to the 'open' array
      if (idx === -1) {
        detailRowsOrders.push(tr.attr('id'));
      }
    }
  });
}
